import {
  AnniProjectDetailProps,
  AnniProjectActionType,
  AnniProjectMessage,
} from '../AnniProjectPopupInterface';
import AnniProjectSnsButton from '../share/AnniProjectSnsButton';
import {
  AnniversaryProjectControllerService,
  anniversaryProjectVo,
} from '@/__generated__/FrontApi';
import ImgNodata from '@/assets/img/anniversary/img_nodata.png';
import Button from '@/components/Button';
import Col from '@/components/Grid/Col';
import Row from '@/components/Grid/Row';
import Image from '@/components/Image';
import { breakpoint, breakpointReverse } from '@/helpers/BreakpointHelper';
import { useUserStore } from '@/stores/UserStore';
import moment from 'moment';
import React, { FC, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

const ContentSection = styled.div`
  margin: 30px 0;
  max-width: 768px;
  display: inline-block;
  text-align: initial;
  width: 100%;

  &.no-data {
    text-align: center;
  }

  ${Row}.anni-info {
    flex-wrap: wrap;

    ${Col} {
      align-self: normal;

      &.anni-info-img {
        /*
        width: 100%;
        min-height: 366px;
        overflow: hidden;
        font-size: 0;
        background-color: #efefef;
        */
        picture {
          position: relative;
          width: 100%;
          min-height: 366px;
          &::after {
            display: block;
            content: '';
            padding-bottom: 100%;
          }
          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }
      }

      ${breakpointReverse(`tablet`)} {
        &:nth-child(2n-1) {
          flex: 367px;
          width: 367px;
        }

        &:nth-child(2n) {
          flex: calc(100% - 367px);
          width: calc(100% - 367px);
          padding: 0 0 0 32px;
        }
        &:nth-child(3) {
          margin: 21px 0 0 0;
        }
        &:nth-child(4) {
          margin: -74px 0 0 0;
        }
      }

      .anni-info-msgbox {
        border: solid 1px #e5e6e8;
        height: 229px;
        width: 100%;
        padding: 16px;
        overflow-y: auto;
        white-space: pre-wrap;

        &::-webkit-scrollbar {
          width: 4px;
          background: #e5e6e8;
        }

        &::-webkit-scrollbar-thumb {
          border-radius: 2px;
          background: #1cabe2;
        }
      }

      .anni-info-link {
        background: #f8f9fa;
        display: table;
        padding: 16px;
        text-align: center;
      }
    }
  }

  ${breakpoint(`tablet`)} {
    margin: 0 0 32px 0;

    ${Row}.anni-info {
      display: inline-block;

      ${Col} {
        margin-top: 30px;
        width: 100%;
        display: inline-block;

        &.anni-info-img {
          min-height: 335px;
        }

        &:nth-child(2n) {
          padding: 0 0 0 0;
        }

        .anni-info-msgbox {
          height: 185px;
        }
      }
    }
  }
`;

const DataList = styled.dl<{ isVertical: boolean }>`
  dt:before,
  dt::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 4px;
    background: #1cabe2;
    border-radius: 100%;
    vertical-align: middle;
    margin: -4px 10px 0 0;
  }

  dt,
  dd {
    height: 31px;
    overflow: hidden;
    float: ${({ isVertical }) => (isVertical ? `left` : `initial`)};
  }
  dt {
    font-weight: bold;
    width: ${({ isVertical }) => (isVertical ? `128px` : `100%`)};
  }
  dd {
    width: ${({ isVertical }) => (isVertical ? `calc(100% - 128px)` : `100%`)};
  }
`;

const PopNoti = styled.div`
  background-color: #f8f9fa;
  padding: 21px;
  margin: 49px 0 0 0;
  ${breakpoint(`mobile`)} {
    margin: 62px 0 0 0;
    & > span {
      display: inline-block;
    }
  }
`;

const AnniProjectComplete: FC<AnniProjectDetailProps> = ({ ...props }) => {
  const defaultTitle = '';
  const userStore = useUserStore();
  const [items, setItems] = useState<any>();
  const [isLoadData, setIsLoadData] = useState(false);
  const [anniImage, setAnniImage] = useState('');

  const loadItems = useCallback(async () => {
    props.setPopupTitle(defaultTitle);
    setIsLoadData(false);
    setItems(null);

    AnniversaryProjectControllerService.selectAdminAnniversaryProjectDetailGet({
      indexNumber: props.id,
    })
      .then(({ data, resultCode }) => {
        const resData = data as anniversaryProjectVo;
        setItems(resData);
        if (resultCode === `success`) {
          props.setPopupTitle(resData?.aprjtName || defaultTitle);
          setAnniImage(
            resData?.aprjtAttImg && resData?.imgAttImgTn
              ? resData?.imgAttImgTn
              : `${process.env.SELF_URL}${ImgNodata}`,
          );
        }
        setIsLoadData(true);
      })
      .catch(() => {
        setIsLoadData(false);
      });
  }, [props]);

  useEffect(() => {
    if (!userStore.user) {
      return;
    }
    setAnniImage(ImgNodata);
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.user, props.id]);

  /*
  // 이미지 배경 처리 방식일 경우만 필요
  const [mainImgStyle, setMainImgStyle] = useState('');
  const loadImageCss = (url: string) => {
    const tmpStyle = `background: url(${url || ImgNodata}) 50% no-repeat;`;
    setMainImgStyle(tmpStyle || '');
  };
  useEffect(() => {
    loadImageCss(items?.aprjtAttImg || '' ? items?.imgAttImgTn : '');
  }, [items]);
  */

  if (isLoadData === false)
    return (
      <ContentSection className="no-data">
        {AnniProjectMessage.PageLoading}
      </ContentSection>
    );

  if (!items) {
    props.setPopupTitle(AnniProjectMessage.DefaultTitle);
    return (
      <ContentSection className="no-data">
        {AnniProjectMessage.PageNotFound}
      </ContentSection>
    );
  }

  /* ********************* 정상적인 접근일 경우 페이지 랜더링 ****************** */

  return (
    <ContentSection>
      <Row className="anni-info">
        {/* // 이미지 배경처리 방식
        <Col className="anni-info-img" css={mainImgStyle}>
          기념기부 대표이미지
        </Col>
        */}
        <Col className="anni-info-img">
          <Image
            pcSrc={anniImage}
            mobileSrc={anniImage}
            onError={() => {
              setAnniImage(ImgNodata);
            }}
          />
        </Col>
        <Col>
          <DataList isVertical={false}>
            <dt>메시지</dt>
            <dd className="anni-info-msgbox">{items?.aprjtMessage}</dd>
          </DataList>
        </Col>
        <Col>
          <DataList isVertical>
            <dt>기념일 종류</dt>
            <dd>{items?.anniTypeName}</dd>

            <dt>기념일</dt>
            <dd>{items?.anniDate}</dd>

            <dt>목표금액</dt>
            <dd>{items?.goalAmount?.toLocaleString() || 0}</dd>

            <dt>목표기간</dt>
            <dd>
              {items?.goalStartDate
                ? moment(items.goalStartDate).format('YY.MM.DD')
                : ''}
              <span> ~ </span>
              {items?.goalEndDate
                ? moment(items.goalEndDate).format('YY.MM.DD')
                : ''}
            </dd>
          </DataList>
        </Col>
        <Col>
          <DataList isVertical={false}>
            <dt>링크</dt>
            <dd className="anni-info-link">
              <AnniProjectSnsButton
                text={items?.aprjtName || '유니세프 기념기부'}
                url={items?.anniSnsShareUrl || ``}
                imgUrl={anniImage || ''}
              />
            </dd>
          </DataList>
          <Button
            type="button"
            value="내 페이지 보러가기"
            full
            css={`
              margin: 40px 0 0 0;
            `}
            onClick={() => {
              props.goto(AnniProjectActionType.admin, items?.aprjtIdx || 0);
            }}
          >
            내 페이지 보러가기
          </Button>
        </Col>
      </Row>
      <PopNoti>
        <span>‘같이 하는 기념기부’ 캠페인에 참여해 주셔서 </span>
        <span>감사합니다.</span>
        <br />
        <span>만드신 페이지는 자동발송 메일 및 </span>
        <span>유니세프한국위원회 기념기부 페이지에서 </span>
        <span>확인하실 수 있습니다.</span>
      </PopNoti>
    </ContentSection>
  );
};

export default AnniProjectComplete;
